import { useEffect, useState } from 'react'
import { FlaskConical, Plus, Trash2 } from 'lucide-react'
import { Select, adminMutate, useToast } from '../admin/primitives.js'
import { TextInput } from '../admin/form.js'
import type { VenueDetail } from './VenueDetailModal.js'

/**
 * Types de sources ajoutables à la main — le vocabulaire utilisateur, pas
 * les valeurs techniques du modèle.
 */
const SOURCE_TYPE_OPTIONS = [
  { value: 'WEBSITE', label: 'Site officiel' },
  { value: 'EVENT_PAGE', label: 'Page agenda / événements' },
  { value: 'PUBLIC_TICKETING', label: 'Billetterie publique' },
  { value: 'RSS', label: 'Flux RSS / Atom' },
  { value: 'ICS', label: 'Calendrier iCalendar (.ics)' },
]
const STATE_FR: Record<string, string> = {
  HEALTHY: 'saine', STALE: 'pas revue récemment', UNAVAILABLE: 'injoignable',
  UNKNOWN: 'jamais relevée', NOT_CONFIGURED: 'non configurée',
}

/**
 * CONFIGURATION AVANCÉE D'UN ÉTABLISSEMENT — sources et recherches.
 *
 * Ces sections vivaient dans un tiroir qui s'ouvrait au clic sur
 * l'établissement. Elles sont désormais rendues DANS la fiche, en mode
 * édition : une cascade modale → tiroir → modale fait perdre le contexte,
 * et ces réglages appartiennent à la même fiche que le reste.
 */
export function VenueAdvancedSections({ detail, canManage, onChanged }: {
  detail: VenueDetail | null
  canManage: boolean
  onChanged: () => void
}) {
  const toast = useToast()
  const [newQuery, setNewQuery] = useState('')
  const [newSource, setNewSource] = useState({ type: 'EVENT_PAGE', url: '' })
  const [testResult, setTestResult] = useState<{
    query: string; duration_ms: number
    results: { provider: string; source: string; configured: boolean; matched: boolean
      signals: { type: string; summary: string }[]; error: string | null }[]
  } | null>(null)
  const [testing, setTesting] = useState<string | null>(null)

  useEffect(() => { setTestResult(null) }, [detail?.venue.id])

  if (detail === null) return null
  const v = detail.venue

  const addQuery = async () => {
    if (!newQuery.trim()) return
    const r = await adminMutate<{ ok?: boolean; error?: string }>('POST', `/api/v1/soirees/market/venues/${v.id}/queries`, {
      query_template: newQuery.trim(),
    })
    if (r.status === 200) { setNewQuery(''); onChanged() }
  }

  const toggleQuery = async (id: string, enabled: boolean) => {
    await adminMutate('PATCH', `/api/v1/soirees/market/queries/${id}`, { enabled })
    onChanged()
  }

  const deleteQuery = async (id: string) => {
    const r = await adminMutate<{ error?: string }>('DELETE', `/api/v1/soirees/market/queries/${id}`)
    if (r.status === 409) toast('error', 'Une recherche automatique se désactive, elle ne se supprime pas.')
    onChanged()
  }

  const testQuery = async (id: string) => {
    setTesting(id)
    setTestResult(null)
    const r = await adminMutate<NonNullable<typeof testResult> & { error?: string }>('POST', `/api/v1/soirees/market/queries/${id}/test`)
    setTesting(null)
    if (r.status === 200 && r.data) setTestResult(r.data)
    else toast('error', 'Test impossible.')
  }

  const addSource = async () => {
    if (!newSource.url.trim().startsWith('https://')) { toast('error', 'URL https requise.'); return }
    const r = await adminMutate<{ ok?: boolean; error?: string }>('POST', `/api/v1/soirees/market/venues/${v.id}/sources`, {
      type: newSource.type, url: newSource.url.trim(),
    })
    if (r.status === 200) { setNewSource({ type: 'EVENT_PAGE', url: '' }); onChanged() }
  }

  return (
    <>
      {/* SOURCES */}
      <div className="so-factors">
        <h3 className="so-factors__title">Sources</h3>
        {detail.sources.length === 0 ? <p className="lc-hint">Aucune source — ajoutez la page agenda du lieu.</p> : (
          <ul>
            {detail.sources.map((s) => (
              <li key={s.id}>
                <strong>{SOURCE_TYPE_OPTIONS.find((o) => o.value === s.type)?.label ?? s.label ?? s.type}</strong>
                {s.account_handle ? ` @${s.account_handle}` : ''}
                {s.url ? <> · <a href={s.url} target="_blank" rel="noopener noreferrer">{new URL(s.url).hostname}</a></> : ''}
                {' · '}{STATE_FR[s.state] ?? s.state}
                {s.last_error ? ` · ⚠ ${s.last_error.slice(0, 60)}` : ''}
                {canManage ? (
                  <>
                    {' '}
                    <button type="button" className="lc-btn lc-btn--icon" aria-label="Basculer la source"
                      onClick={() => void adminMutate('PATCH', `/api/v1/soirees/market/sources/${s.id}`, { enabled: !s.enabled }).then(onChanged)}>
                      {s.enabled ? '⏸' : '▶'}
                    </button>
                    <button type="button" className="lc-btn lc-btn--icon" aria-label="Supprimer la source"
                      onClick={() => void adminMutate('DELETE', `/api/v1/soirees/market/sources/${s.id}`).then(onChanged)}>
                      <Trash2 size={12} />
                    </button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {canManage ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <Select compact value={newSource.type} options={SOURCE_TYPE_OPTIONS} ariaLabel="Type de source"
              onChange={(val) => setNewSource({ ...newSource, type: val })} />
            <TextInput value={newSource.url} onChange={(val) => setNewSource({ ...newSource, url: val })}
              placeholder="https://…" maxLength={500} ariaLabel="URL de la source" />
            <button type="button" className="lc-btn" onClick={() => void addSource()}><Plus size={13} /> Source</button>
          </div>
        ) : null}
      </div>

      {/*
        * RECHERCHES DE VEILLE — repliées, et pour une raison chiffrée.
        *
        * Audit du 25/08/2026 sur les données réelles : 68 recherches, toutes
        * exécutées, 4 ayant produit la moindre correspondance, 1 résultat
        * cumulé, ZÉRO événement concurrent. Les cinq événements connus
        * viennent tous de Stories Instagram, et aucun moteur de recherche
        * n'est configuré.
        *
        * On ne supprime rien — la capacité reste, et la décision revient à
        * l'exploitant. Mais ces réglages cessent d'occuper le premier plan
        * d'une fiche : leur rendement ne le justifie pas.
        */}
      <details className="so-factors so-queries">
        <summary className="so-queries__sum">
          <span className="so-factors__title">Recherches de veille</span>
          <span className="lc-hint">
            {detail.queries.length} recherche{detail.queries.length > 1 ? 's' : ''}
            {' · '}
            {detail.queries.filter((q) => q.enabled).length} active
            {detail.queries.filter((q) => q.enabled).length > 1 ? 's' : ''}
          </span>
        </summary>
        <p className="lc-hint so-queries__note">
          Ces recherches filtrent le contenu déjà collecté par les autres sources.
          Sans moteur de recherche configuré, elles ne vont rien chercher d’elles-mêmes —
          et leur rendement observé est très faible.
        </p>
        <ul>
          {detail.queries.map((q) => (
            <li key={q.id} className={q.enabled ? '' : 'so-query--off'}>
              <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={q.enabled} disabled={!canManage}
                  onChange={(e) => void toggleQuery(q.id, e.target.checked)}
                  aria-label={`Activer « ${q.query_template} »`} />
                {q.query_template}
              </label>
              {' '}<em className="lc-hint">{q.query_type === 'AUTO' ? 'auto' : 'personnalisée'}
                {q.priority_level === 'HIGH' ? ' · prioritaire' : ''}</em>
              {canManage ? (
                <>
                  {' '}
                  <button type="button" className="lc-btn lc-btn--icon" aria-label={`Tester « ${q.query_template} »`}
                    disabled={testing !== null} onClick={() => void testQuery(q.id)}>
                    <FlaskConical size={12} />
                  </button>
                  {q.query_type === 'CUSTOM' ? (
                    <button type="button" className="lc-btn lc-btn--icon" aria-label="Supprimer la recherche"
                      onClick={() => void deleteQuery(q.id)}><Trash2 size={12} /></button>
                  ) : null}
                </>
              ) : null}
            </li>
          ))}
        </ul>
        {canManage ? (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <TextInput value={newQuery} onChange={setNewQuery}
              placeholder="Ex. Espace Club Rennes concert gratuit" maxLength={200} ariaLabel="Nouvelle recherche" />
            <button type="button" className="lc-btn" onClick={() => void addQuery()}><Plus size={13} /> Ajouter</button>
          </div>
        ) : null}
        {testing ? <p className="lc-hint" aria-busy="true">Test en cours (collecte réelle des sources)…</p> : null}
        {testResult ? (
          <div className="so-testresult">
            <p className="lc-hint" style={{ margin: '8px 0 4px' }}>
              « {testResult.query} » · {testResult.duration_ms.toLocaleString('fr-FR')} ms — rien n'a été enregistré.
            </p>
            <ul>
              {testResult.results.map((r, i) => (
                <li key={i}>
                  <strong>{r.source}</strong> ({r.provider}) :{' '}
                  {!r.configured ? 'provider non configuré'
                    : r.error ? `échec — ${r.error}`
                      : `${r.matched ? 'correspondance trouvée' : 'pas de correspondance'} · ${r.signals.length} signal(aux) détecté(s)`}
                  {r.signals.length > 0 ? (
                    <ul>{r.signals.map((sg, k) => <li key={k}>{sg.type} — {sg.summary}</li>)}</ul>
                  ) : null}
                </li>
              ))}
              {testResult.results.length === 0 ? <li>Aucune source active à tester.</li> : null}
            </ul>
          </div>
        ) : null}
      </details>
    </>
  )
}
